import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileText, BarChart3, CheckCircle, TrendingUp, Target, BookOpen, Sparkles, Zap, Play, BookMarked } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import LandingStyleHero from '../components/productHero/LandingStyleHero';
import LaunchAnnouncementBanner from '../components/LaunchAnnouncementBanner';
import ProductDemoVideoEmbed from '../components/video/ProductDemoVideoEmbed';
import { getWalkthroughVideo } from '../config/walkthroughVideos';
import SEO from '../components/SEO';
import PremiumAccessPrompt from '../components/PremiumAccessPrompt';
import { usePremiumProductAccess } from '../hooks/usePremiumProductAccess';
import { PREMIUM_PRODUCT_KEYS } from '../config/premiumProducts';
import useAuthUser from '../services/useAuthUser';
import { PRODUCTION_PATHS, resolveHostedAppUrl } from '../config/siteUrl';

const REDROCK_BOOTSTRAP_TOKEN_KEY = 'cognidoor.redrock.bootstrap.firebase_token';

function getRedRockAppUrl() {
  return resolveHostedAppUrl({
    envName: 'REACT_APP_REDROCK_APP_URL',
    localhostUrl: 'http://localhost:8090',
    productionPath: PRODUCTION_PATHS.redrockApp,
  });
}

function getHostedRedRockNextPath(baseUrl, currentSuffix = '') {
  try {
    const url = new URL(baseUrl);
    const basePath = url.pathname && url.pathname !== '/' ? url.pathname.replace(/\/$/, '') : '';
    const suffix = currentSuffix ? `${currentSuffix}` : '';
    return `${basePath}${suffix}` || '/';
  } catch {
    return '/redrock-app';
  }
}

async function handoffToRedRockApp(user, currentSuffix = '') {
  const baseUrl = getRedRockAppUrl().replace(/\/$/, '');
  const nextPath = getHostedRedRockNextPath(baseUrl, currentSuffix);
  const bootstrapUrl = `${baseUrl}/auth/bootstrap?next=${encodeURIComponent(nextPath)}`;

  try {
    if (user?.getIdToken) {
      const token = await user.getIdToken();
      window.sessionStorage.setItem(REDROCK_BOOTSTRAP_TOKEN_KEY, token);
    } else {
      window.sessionStorage.removeItem(REDROCK_BOOTSTRAP_TOKEN_KEY);
    }
  } catch {
    window.sessionStorage.removeItem(REDROCK_BOOTSTRAP_TOKEN_KEY);
  }

  window.location.assign(bootstrapUrl);
}

const sectionTitleClass =
  'text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent';

const glassCardClass =
  'rounded-2xl border border-slate-200/80 bg-white/95 shadow-lg shadow-slate-200/50 p-5 sm:p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:border-sky-200/60 hover:-translate-y-0.5';

const whatYouGet = [
  { icon: FileText, title: 'Timed assessment shell', text: 'Work in a real simulator with a top timer bar, left stage rail, center workspace, and right utility rail—the same structure you need to perform under pressure.' },
  { icon: BarChart3, title: 'Evidence, report, and graph tasks', text: 'Collect the right evidence, do the math, write the report, choose the chart, and carry the logic into mini-cases. Every step is scored.' },
  { icon: CheckCircle, title: 'Deterministic results and coaching', text: 'Finish with a clear, evidence-based review: section scores, journal alignment, and a next-case recommendation—no vague feedback.' },
];

const howItWorks = [
  { icon: FileText, title: 'Study and investigate', text: 'Read the brief, work through Investigation, and collect evidence into your Research Journal. The journal is scored—use it to support your answers.' },
  { icon: BarChart3, title: 'Analyse and summarise', text: 'Answer analysis questions, fill the report blanks, complete graph selection and visualization tasks. The calculator and journal stay available.' },
  { icon: CheckCircle, title: 'Complete the cases', text: 'Transfer your logic into mini-cases. Your run is tracked consistently so your practice stays structured and reviewable.' },
  { icon: TrendingUp, title: 'Review with deterministic coaching', text: 'See exactly where you lost points, how your evidence and report held up, and what to fix on your next run.' },
];

const whyThisHelps = [
  { icon: Target, title: 'Serious assessment environment', text: 'The simulator feels like the real thing—dense, timed, and structured—so the format stops feeling new when it matters.' },
  { icon: BookOpen, title: 'Evidence-backed feedback', text: 'You get section-level and process-level review, not generic praise. Know what to change before your next attempt.' },
  { icon: TrendingUp, title: 'Build confidence through reps', text: 'Seventeen cases, one shared shell. Repeat until your evidence selection, reporting, and transfer logic become natural.' },
];

const relatedLinks = [
  { label: 'Sea Wolf Simulation', path: '/seawolf-practice', Icon: BookOpen },
  { label: 'BCG Casey Simulation', path: '/bcg-casey-simulation', Icon: Sparkles },
  { label: 'Consulting Math Guides', path: '/consulting-math-guides', Icon: BarChart3 },
  { label: 'About McKinsey Solve', path: '/about-solve', Icon: Zap },
];

function RedRockPreviewPanel() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.06)_0%,rgba(12,34,66,0.12)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/60 to-transparent" />
      <div className="relative border-b border-sky-400/20 p-4 text-white bg-slate-900/40">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-sky-200/90">Red Rock Simulator</span>
          <span className="rounded-full bg-sky-500/20 px-2.5 py-1 text-[11px] font-semibold text-sky-200">Study → Cases</span>
        </div>
      </div>
      <div className="relative p-4">
        <div className="rounded-2xl border border-white/15 overflow-hidden bg-white/5 backdrop-blur-sm ring-1 ring-white/5 shadow-[0_16px_36px_-24px_rgba(2,6,23,0.78)] min-h-[280px] flex items-center justify-center">
          <img
            src="/images/landing/redrock-preview.png"
            alt="Red Rock simulator — Investigation, exhibits, Research Journal, coach feedback"
            className="w-full h-full object-contain object-center"
          />
        </div>
      </div>
    </>
  );
}

function RedRockProofSection({ onStart, checkingAccess }) {
  const [starting, setStarting] = useState(false);
  const video = getWalkthroughVideo('redRock2026');

  const handleStartRedRock = () => {
    onStart(() => setStarting(true));
  };

  return (
    <section id="explore" className="py-20 sm:py-28 bg-white px-4 sm:px-8 md:px-12 lg:px-16">
      <div className="max-w-[120rem] mx-auto">
        <h2 className={sectionTitleClass + ' text-center mb-8 sm:mb-10'}>What your feedback actually shows</h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12 sm:mb-14 text-sm sm:text-base">
          Each run gives you something you can use on the next one: where your evidence selection held up, where your analysis or report slipped, and the next move that would improve the run most.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.9fr] gap-8 lg:gap-10 items-center">
          <div>
            <div className="rounded-2xl border border-slate-200/80 bg-white/95 shadow-xl overflow-hidden">
            <ProductDemoVideoEmbed videoKey="redRock2026" />
            <div className="px-4 py-2.5 border-t border-slate-100 bg-white">
              <div className="text-sm font-semibold text-gray-900">{video.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{video.description}</div>
            </div>
            </div>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Evidence', text: 'See whether you collected the right signals and used the journal well.' },
              { label: 'Analysis', text: 'See whether your answers and reasoning supported the recommendation.' },
              { label: 'Report and graph', text: 'See whether your report blanks and chart choices were on target.' },
              { label: 'Next move', text: 'Know exactly what to change on the next rep.' },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-3 rounded-xl border border-slate-200/80 bg-white p-5 text-left"
              >
                <span className="text-sky-600 font-semibold text-base shrink-0">{item.label}:</span>
                <span className="text-gray-600 text-base">{item.text}</span>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.24 }}
              className="pt-2"
            >
              <button
                type="button"
                onClick={handleStartRedRock}
                disabled={starting || checkingAccess}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition w-full sm:w-auto justify-center disabled:opacity-80 disabled:pointer-events-none min-w-[180px]"
              >
                {starting || checkingAccess ? (
                  <>
                    <LoadingSpinner size="sm" tone="onPrimary" className="shrink-0" /> {checkingAccess ? 'Checking access…' : 'Starting…'}
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" strokeWidth={2} /> Start Red Rock Simulation
                  </>
                )}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function RedRockPage() {
  const navigate = useNavigate();
  const redRockAccess = usePremiumProductAccess(PREMIUM_PRODUCT_KEYS.redRockSimulation);
  const { user } = useAuthUser();

  const handleScrollToProof = () => document.getElementById('explore')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleScrollToWalkthrough = () => document.getElementById('explore')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleViewPricing = () => navigate('/', { state: { scrollToPricing: true } });
  const handleSeeDemo = () => navigate('/', { state: { scrollToLiveDemos: true, track: 'mck', demoVariantMck: 'redrock' } });
  const handleStartRedRock = (onAllowed) => {
    redRockAccess.attemptAccess(async () => {
      onAllowed();
      await handoffToRedRockApp(user);
    }, {
      returnTo: '/redrock-app',
      onDemo: handleSeeDemo,
    });
  };

  return (
    <>
      <SEO
        title="Red Rock Simulator | Cognidoor"
        description="Practise Red Rock in a serious deterministic simulator. Timed assessment shell, 17-case library, Research Journal, report and graph tasks, Coach and Exam Replica modes."
        url="/redrock"
        keywords={['red rock simulator', 'mckinsey solve', 'red rock practice', 'consulting cases', 'assessment prep']}
      />
      <div className="bg-white text-gray-900 font-sans" style={{ '--cog-blue-1': '#277AB2', '--cog-blue-2': '#0B5996', '--cog-blue-3': '#082B4A' }}>
        <LandingStyleHero
          track="mck"
          headline="Practise Red Rock in a serious simulator built for pressure, structure, and clear thinking."
          subheadline="Work through dense cases in a timed assessment shell, collect the right evidence, do the math, write the report, choose the chart, and carry the logic into mini-cases. Review your performance with deterministic, evidence-based coaching."
          eyebrowChips={['17-case simulator', 'Coach + Exam Replica', 'Deterministic coaching']}
          proofRow={[
            { value: '17-case simulator library' },
            { value: 'Coach and Exam Replica modes' },
            { value: 'Research Journal with process scoring' },
          ]}
          primaryCtaLabel="Start Red Rock Simulation"
          onPrimaryCta={handleScrollToProof}
          secondaryCtaLabel="Watch walkthrough"
          onSecondaryCta={handleScrollToWalkthrough}
          announcement={
            <LaunchAnnouncementBanner
              track="mck"
              badge="Red Rock update"
              title="New ecological cases. Sharper coaching after every run."
              body="Practice fresh ecology-style Red Rock scenarios, then review what changed the outcome: evidence, units, report logic, graph choice, and the transfer step into mini-cases."
              primaryCta="Try Red Rock"
              onPrimaryCta={handleScrollToProof}
            />
          }
          previewChildren={<RedRockPreviewPanel />}
        />

        <section id="what-you-get" className="py-14 sm:py-20 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-10 sm:mb-14'}>What you get from a Red Rock run</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whatYouGet.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <RedRockProofSection
          onStart={handleStartRedRock}
          checkingAccess={redRockAccess.isLoading}
        />

        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/40 to-white px-4 sm:px-8 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-600 text-sm sm:text-base mb-5">
              New to Red Rock? Read the guide to learn the format, cases, and how to get the most from the simulator.
            </p>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/redrock-guide')}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-sky-200 bg-white px-5 py-3 text-sm font-semibold text-sky-700 shadow-sm hover:border-sky-300 hover:bg-sky-50/80 transition-colors"
            >
              <BookMarked className="w-4 h-4" strokeWidth={2} /> Open Red Rock Guide
            </motion.button>
          </div>
        </section>

        <section id="how-it-works" className="py-14 sm:py-20 bg-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-10 sm:mb-14'}>How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-14 sm:py-20 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-10 sm:mb-14'}>Why this helps before Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyThisHelps.map((item, i) => (
            <motion.div 
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                </motion.div>
            ))}
          </div>
        </section>

        <section className="py-14 sm:py-20 bg-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-12'}>Build your McKinsey prep stack</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6 text-sm">
            Red Rock Simulation is for assessment-style practice in a serious shell. Pair it with Sea Wolf for ecosystem-style decisions and Casey for BCG-style cases.
          </p>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            {relatedLinks.map(({ label, path, Icon }) => (
              <motion.button
                key={path}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(path)}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:border-sky-300 hover:bg-sky-50/50 hover:text-sky-800 transition-colors"
              >
                <Icon className="w-4 h-4" strokeWidth={2} /> {label}
                  </motion.button>
            ))}
          </div>
          <p className="mt-8 text-sm text-gray-500">
            <button type="button" onClick={handleViewPricing} className="font-semibold text-sky-600 hover:text-sky-700 underline underline-offset-2">
              See plans and pricing
            </button>
          </p>
        </section>
      </div>
      <PremiumAccessPrompt {...redRockAccess.promptProps} />
    </>
  );
}
